import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 cursor-default items-center justify-center gap-0 overflow-hidden border py-1 font-medium whitespace-nowrap ring-offset-background transition-colors aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [a&]:cursor-pointer [a&]:focus-visible:ring-2 [a&]:focus-visible:ring-ring [a&]:focus-visible:ring-offset-2 [a&]:focus-visible:outline-none [button&]:cursor-pointer [button&]:focus-visible:ring-2 [button&]:focus-visible:ring-ring [button&]:focus-visible:ring-offset-2 [button&]:focus-visible:outline-none',
  {
    variants: {
      type: {
        default: 'border-transparent',
        secondary: 'border-transparent',
        outline: '',
        bordered: 'rounded border-primary-foreground shadow-sm',
      },
      flavour: {
        informational: '',
        successful: '',
        warning: '',
        ai: '',
        destructive: '',
        neutral: '',
      },
      rounded: { true: 'rounded', false: 'rounded-md' },
      size: {
        xs: 'px-2 text-xs [&_button_svg]:size-3.5',
        sm: 'px-3 text-sm [&_button_svg]:size-4',
        md: 'px-4 text-base [&_button_svg]:size-4',
      },
      circular: { true: '', false: '' },
    },
    compoundVariants: [
      { size: 'xs', circular: true, className: 'h-5 min-w-5 p-0' },
      { size: 'sm', circular: true, className: 'h-6 min-w-6 p-0' },
      { size: 'md', circular: true, className: 'h-7 min-w-7 p-0' },
      { circular: true, rounded: true, className: 'rounded-full' },
      // default type with different flavours
      {
        type: 'default',
        flavour: 'informational',
        className:
          'bg-primary text-primary-foreground [a&]:hover:bg-primary-hover [button&]:hover:bg-primary-hover',
      },
      {
        type: 'default',
        flavour: 'successful',
        className:
          'bg-success text-foreground [a&]:hover:bg-success/90 [button&]:hover:bg-success/90',
      },
      {
        type: 'default',
        flavour: 'warning',
        className:
          'bg-warning text-background [a&]:hover:bg-warning/90 [button&]:hover:bg-warning/90',
      },
      {
        type: 'default',
        flavour: 'ai',
        className: 'bg-ai text-background [a&]:hover:bg-ai/90 [button&]:hover:bg-ai/90',
      },
      {
        type: 'default',
        flavour: 'destructive',
        className:
          'bg-destructive text-background [a&]:hover:bg-destructive/90 [button&]:hover:bg-destructive/90',
      },
      {
        type: 'default',
        flavour: 'neutral',
        className:
          'bg-muted-foreground text-background [a&]:hover:bg-muted-foreground/90 [button&]:hover:bg-muted-foreground/90',
      },

      // secondary type with different flavours
      {
        type: 'secondary',
        flavour: 'informational',
        className:
          'bg-info-soft text-primary [a&]:hover:bg-info-soft/80 [button&]:hover:bg-info-soft/80',
      },
      {
        type: 'secondary',
        flavour: 'successful',
        className:
          'bg-success-soft text-success [a&]:hover:bg-success-soft/80 [button&]:hover:bg-success-soft/80',
      },
      {
        type: 'secondary',
        flavour: 'warning',
        className:
          'bg-warning-soft text-warning [a&]:hover:bg-warning-soft/80 [button&]:hover:bg-warning-soft/80',
      },
      {
        type: 'secondary',
        flavour: 'ai',
        className: 'bg-ai-soft text-ai [a&]:hover:bg-ai-soft/80 [button&]:hover:bg-ai-soft/80',
      },
      {
        type: 'secondary',
        flavour: 'destructive',
        className:
          'bg-destructive-soft text-destructive [a&]:hover:bg-destructive-soft/80 [button&]:hover:bg-destructive-soft/80',
      },
      {
        type: 'secondary',
        flavour: 'neutral',
        className:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-accent [button&]:hover:bg-accent',
      },

      // outline type with different flavours
      {
        type: 'outline',
        flavour: 'informational',
        className:
          'border-foreground bg-transparent text-foreground [a&]:hover:bg-info-soft [button&]:hover:bg-info-soft',
      },
      {
        type: 'outline',
        flavour: 'successful',
        className:
          'border-success bg-transparent text-success [a&]:hover:bg-success-soft [button&]:hover:bg-success-soft',
      },
      {
        type: 'outline',
        flavour: 'warning',
        className:
          'border-warning bg-transparent text-warning [a&]:hover:bg-warning-soft [button&]:hover:bg-warning-soft',
      },
      {
        type: 'outline',
        flavour: 'ai',
        className:
          'border-ai bg-transparent text-ai [a&]:hover:bg-ai-soft [button&]:hover:bg-ai-soft',
      },
      {
        type: 'outline',
        flavour: 'destructive',
        className:
          'border-destructive bg-transparent text-destructive [a&]:hover:bg-destructive-soft [button&]:hover:bg-destructive-soft',
      },
      {
        type: 'outline',
        flavour: 'neutral',
        className:
          'border-border bg-transparent text-foreground [a&]:hover:bg-muted/50 [button&]:hover:bg-muted/50',
      },

      // bordered type with different flavours
      {
        type: 'bordered',
        flavour: 'informational',
        className:
          'bg-foreground text-background [a&]:hover:bg-foreground/90 [button&]:hover:bg-foreground/90',
      },
      {
        type: 'bordered',
        flavour: 'successful',
        className:
          'bg-success text-background [a&]:hover:bg-success/90 [button&]:hover:bg-success/90',
      },
      {
        type: 'bordered',
        flavour: 'ai',
        className: 'bg-ai text-background [a&]:hover:bg-ai/90 [button&]:hover:bg-ai/90',
      },
      {
        type: 'bordered',
        flavour: 'neutral',
        className:
          'bg-muted-foreground text-background [a&]:hover:bg-muted-foreground/90 [button&]:hover:bg-muted-foreground/90',
      },
      {
        type: 'bordered',
        flavour: 'warning',
        className:
          'bg-warning text-background [a&]:hover:bg-warning/90 [button&]:hover:bg-warning/90',
      },
      {
        type: 'bordered',
        flavour: 'destructive',
        className:
          'bg-destructive text-background [a&]:hover:bg-destructive/90 [button&]:hover:bg-destructive/90',
      },
    ],
    defaultVariants: { type: 'secondary', flavour: 'neutral' },
  }
);

type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    /**
     * When `true`, renders as the consumer's element via Radix `Slot`.
     * Children MUST be a single React element — plain text or fragments
     * will throw "Slot expects a single child element".
     */
    asChild?: boolean;
    rounded?: boolean;
    circular?: boolean;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
  };

const Badge = ({
  className,
  type,
  flavour,
  asChild = false,
  rounded = true,
  circular = false,
  size = 'xs',
  leadingIcon,
  trailingIcon,
  children,
  ref,
  ...props
}: BadgeProps & { ref?: React.Ref<HTMLSpanElement> }) => {
  const Comp = asChild ? Slot : 'span';
  const useAsChild = asChild && React.isValidElement(children);

  const textContent = useAsChild
    ? (children.props as { children?: React.ReactNode }).children
    : children;

  const isDot =
    (textContent == null ||
      textContent === false ||
      textContent === '' ||
      (Array.isArray(textContent) && textContent.length === 0)) &&
    leadingIcon == null &&
    trailingIcon == null;
  const dotSizeMap = { xs: 'h-1.5 w-1.5', sm: 'h-2 w-2', md: 'h-2.5 w-2.5' } as const;
  const dotSize = dotSizeMap[size ?? 'xs'];

  const inner = isDot ? null : (
    <>
      {leadingIcon}
      <span className="block min-w-0 truncate px-1">{textContent}</span>
      {trailingIcon}
    </>
  );

  return (
    <Comp
      ref={ref}
      data-slot="badge"
      className={cn(
        badgeVariants({ type, flavour, rounded, size, circular }),
        isDot && cn('min-w-0 rounded-full p-0', dotSize),
        className
      )}
      {...props}
    >
      {useAsChild ? React.cloneElement(children, undefined, inner) : inner}
    </Comp>
  );
};

export { Badge, badgeVariants, type BadgeProps };
